import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Field = ({ label, name, type = "text", value, onChange, required, testid, multiline }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.28em] uppercase text-black/50 mb-3">
      {label}
      {required && <span className="text-black/30"> *</span>}
    </span>
    {multiline ? (
      <textarea
        data-testid={testid}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={3}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base text-black focus:outline-none focus:border-black transition-colors resize-none"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-0 border-b border-black/20 rounded-none px-0 py-3 text-base text-black focus:outline-none focus:border-black transition-colors"
      />
    )}
  </label>
);

/**
 * Popup form tied to a specific listing — submits to /listings/{slug}/showing-request.
 */
const ShowingRequestModal = ({ listingSlug, listingAddress, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/listings/${listingSlug}/showing-request`, form);
      setSubmitted(true);
      toast.success("Thanks! We'll contact you to confirm your tour.");
      setTimeout(onClose, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send right now — please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="showing-request-modal"
      onClick={onClose}
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Request a private showing"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-white p-8 md:p-12 max-h-[90vh] overflow-y-auto"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="showing-request-close"
          aria-label="Close"
          className="absolute top-5 right-5 text-black/40 hover:text-black transition-colors"
        >
          <X size={22} strokeWidth={1.5} />
        </button>

        {submitted ? (
          <div className="py-10 text-center" data-testid="showing-request-success">
            <p className="overline mb-4">Request received</p>
            <h3 className="font-heading text-3xl tracking-tight mb-4">Thank you.</h3>
            <p className="text-black/60 leading-relaxed">
              A member of the Manzano team will reach out shortly to confirm your private
              showing at <span className="text-black">{listingAddress}</span>.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="showing-request-done"
              className="inline-flex items-center justify-center px-10 py-4 mt-10 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="overline mb-3">Private Showing</p>
            <h3 className="font-heading text-3xl tracking-tight mb-2">Request a tour</h3>
            <p className="text-sm text-black/60 mb-8">{listingAddress}</p>

            <form data-testid="showing-request-form" onSubmit={handleSubmit} className="space-y-7">
              <Field
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                testid="showing-request-input-name"
              />
              <Field
                label="Email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                testid="showing-request-input-email"
              />
              <Field
                label="Phone"
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                required
                testid="showing-request-input-phone"
              />
              <div className="grid grid-cols-2 gap-6">
                <Field
                  label="Preferred Date"
                  type="date"
                  name="preferred_date"
                  value={form.preferred_date}
                  onChange={handleChange}
                  testid="showing-request-input-date"
                />
                <Field
                  label="Preferred Time"
                  type="time"
                  name="preferred_time"
                  value={form.preferred_time}
                  onChange={handleChange}
                  testid="showing-request-input-time"
                />
              </div>
              <Field
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                multiline
                testid="showing-request-input-message"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="showing-request-submit"
                className="w-full inline-flex items-center justify-center px-10 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50"
              >
                {submitting ? "Sending..." : "Submit Request"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ShowingRequestModal;
